import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../utils/supabaseClient';
import Button from '../ui/Button';
import { CheckCircleIcon, XCircleIcon, RefreshIcon } from '../../utils/IconWrapper';
import { ChannelSyncManager } from '../../utils/channelSyncManager';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f1f5f9;
`;

const Title = styled.h2`
  color: #0d9488;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AccommodationList = styled.div`
  display: grid;
  gap: 1rem;
`;

const AccommodationCard = styled.div<{ $enabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid ${props => props.$enabled ? '#10b981' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.$enabled ? '#f0fdf4' : '#f9fafb'};
  transition: all 0.3s ease;
`;

const AccommodationInfo = styled.div`
  flex: 1;
`;

const AccommodationTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.1rem;
`;

const AccommodationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

const StatusBadge = styled.span<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$enabled ? '#dcfce7' : '#f3f4f6'};
  color: ${props => props.$enabled ? '#166534' : '#374151'};
`;

const Controls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;



const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const UrlEditingSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px dashed #cbd5e1;
`;

const UrlInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const UrlLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const UrlInput = styled.input`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: monospace;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
`;

const UrlControls = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const SyncButton = styled(Button)`
  min-width: 140px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Accommodation {
  id: string;
  title_it: string;
  title_en: string;
  sync_enabled: boolean;
  airbnb_ical_url: string | null;
  airbnb_listing_id: string | null;
}

const AirbnbSyncSettings: React.FC = () => {
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingUrls, setEditingUrls] = useState<{ [key: string]: { icalUrl: string; listingId: string } }>({});
  const [syncingAccommodation, setSyncingAccommodation] = useState<string | null>(null);

  useEffect(() => {
    fetchAccommodations();
  }, []);

  const fetchAccommodations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('accommodations')
        .select('id, title_it, title_en, sync_enabled, airbnb_ical_url, airbnb_listing_id')
        .order('title_it');

      if (error) throw error;
      setAccommodations(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSync = async (accommodationId: string, enabled: boolean) => {
    try {
      setSaving(accommodationId);
      setError(null);
      setSuccess(null);

      const { error } = await supabase
        .from('accommodations')
        .update({ sync_enabled: enabled })
        .eq('id', accommodationId);

      if (error) throw error;

      setAccommodations(prev => 
        prev.map(acc => 
          acc.id === accommodationId 
            ? { ...acc, sync_enabled: enabled }
            : acc
        )
      );

      setSuccess(enabled ? 'Sincronizzazione abilitata' : 'Sincronizzazione disabilitata');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const updateIcalUrl = async (accommodationId: string, icalUrl: string, listingId: string) => {
    try {
      setSaving(accommodationId);
      setError(null);
      setSuccess(null);

      const { data, error } = await supabase
        .from('accommodations')
        .update({ 
          airbnb_ical_url: icalUrl || null,
          airbnb_listing_id: listingId || null
        })
        .eq('id', accommodationId)
        .select();

      if (error) {
        console.error('❌ Errore salvando iCal URL:', error);
        throw error;
      }
      
      console.log('✅ iCal URL salvato con successo:', data);

      setAccommodations(prev => 
        prev.map(acc => 
          acc.id === accommodationId 
            ? { ...acc, airbnb_ical_url: icalUrl || null, airbnb_listing_id: listingId || null }
            : acc
        )
      );

      setSuccess('URL iCal aggiornato');
      // Rimuovi dalla modalità editing
      setEditingUrls(prev => {
        const updated = { ...prev };
        delete updated[accommodationId];
        return updated;
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const startEditingUrls = (accommodationId: string, currentIcalUrl: string | null, currentListingId: string | null) => {
    setEditingUrls(prev => ({
      ...prev,
      [accommodationId]: {
        icalUrl: currentIcalUrl || '',
        listingId: currentListingId || ''
      }
    }));
  };

  const cancelEditingUrls = (accommodationId: string) => {
    setEditingUrls(prev => {
      const updated = { ...prev };
      delete updated[accommodationId];
      return updated;
    });
  };

  const updateEditingUrl = (accommodationId: string, field: 'icalUrl' | 'listingId', value: string) => {
    setEditingUrls(prev => ({
      ...prev,
      [accommodationId]: {
        ...prev[accommodationId],
        [field]: value
      }
    }));
  };

  const syncAccommodationNow = async (accommodationId: string) => {
    try {
      setSyncingAccommodation(accommodationId);
      setError(null);
      setSuccess(null);

      const syncManager = ChannelSyncManager.getInstance();
      
      // Trigger sync per questo specifico alloggio
      const results = await syncManager.syncAllChannels([accommodationId]);
      
      if (results && results.length > 0) {
        const result = results[0];
        if (result.status === 'success') {
          setSuccess(`✅ Sincronizzato! ${result.message || 'Sincronizzazione completata'}`);
        } else {
          throw new Error(result.message || 'Errore durante la sincronizzazione');
        }
      } else {
        setSuccess('✅ Sincronizzazione completata');
      }
      
      // Refresh accommodations per mostrare aggiornamenti
      await fetchAccommodations();
      
    } catch (error: any) {
      console.error('Errore sincronizzazione:', error);
      setError(`❌ ${error.message || 'Errore durante la sincronizzazione'}`);
    } finally {
      setSyncingAccommodation(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Caricamento alloggi...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          🏠 Sincronizzazione Selettiva Airbnb
        </Title>
        <Button onClick={fetchAccommodations} disabled={loading}>
          <RefreshIcon /> Aggiorna
        </Button>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <AccommodationList>
        {accommodations.map(accommodation => {
          const isEditing = editingUrls[accommodation.id];
                          const isCurrentlySaving = saving === accommodation.id;
          
          return (
            <AccommodationCard 
              key={accommodation.id} 
              $enabled={accommodation.sync_enabled}
            >
              <AccommodationInfo>
                <AccommodationTitle>{accommodation.title_it}</AccommodationTitle>
                <AccommodationDetails>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    ID: {accommodation.id.substring(0, 8)}...
                  </div>
                  <div style={{ marginBottom: '0.25rem' }}>
                    <strong>Airbnb Listing ID:</strong> {accommodation.airbnb_listing_id || 
                      <span style={{ color: '#dc3545' }}>⚠️ Non configurato</span>
                    }
                  </div>
                  <div style={{ marginBottom: '0.25rem' }}>
                    <strong>URL iCal:</strong> {accommodation.airbnb_ical_url ? 
                      <span style={{ color: '#28a745' }}>✅ Configurato</span> : 
                      <span style={{ color: '#dc3545' }}>⚠️ Non configurato</span>
                    }
                  </div>
                  {accommodation.airbnb_ical_url && accommodation.airbnb_listing_id && (
                    <div style={{ 
                      color: '#28a745', 
                      fontSize: '0.8rem', 
                      marginTop: '0.5rem',
                      padding: '0.25rem 0.5rem',
                      background: '#d4edda',
                      borderRadius: '4px',
                      border: '1px solid #c3e6cb'
                    }}>
                      🎉 Sincronizzazione pronta!
                    </div>
                  )}
                </AccommodationDetails>
                
                {/* Sezione per editing URL iCal */}
                {isEditing && (
                  <UrlEditingSection>
                    <div style={{ 
                      background: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      borderRadius: '6px', 
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <strong>📝 Configura la Sincronizzazione:</strong><br />
                      1. Inserisci il <strong>Listing ID</strong> di Airbnb (lo trovi nell'URL)<br />
                      2. Inserisci l'<strong>URL iCal</strong> dal calendario Airbnb<br />
                      3. Clicca <strong>"💾 SALVA CONFIGURAZIONE"</strong> per completare
                    </div>
                    
                    <UrlInputGroup>
                      <UrlLabel>
                        <strong>Airbnb Listing ID:</strong>
                        <small style={{ display: 'block', color: '#666', fontWeight: 'normal' }}>
                          Es: se il tuo URL è airbnb.com/rooms/12345678, inserisci "12345678"
                        </small>
                      </UrlLabel>
                      <UrlInput
                        type="text"
                        placeholder="12345678"
                        value={isEditing.listingId}
                        onChange={(e) => updateEditingUrl(accommodation.id, 'listingId', e.target.value)}
                      />
                    </UrlInputGroup>
                    
                    <UrlInputGroup>
                      <UrlLabel>
                        <strong>URL iCal (Calendario Airbnb):</strong>
                        <small style={{ display: 'block', color: '#666', fontWeight: 'normal' }}>
                          Host Dashboard → Calendario → Impostazioni → Esporta Calendario
                        </small>
                      </UrlLabel>
                      <UrlInput
                        type="url"
                        placeholder="https://calendar.airbnb.com/calendar/ical/..."
                        value={isEditing.icalUrl}
                        onChange={(e) => updateEditingUrl(accommodation.id, 'icalUrl', e.target.value)}
                      />
                    </UrlInputGroup>
                    
                    <UrlControls>
                      <Button
                        variant="outline"
                        size="medium"
                        onClick={() => cancelEditingUrls(accommodation.id)}
                        disabled={isCurrentlySaving}
                      >
                        ❌ Annulla
                      </Button>
                      <Button
                        variant="primary"
                        size="medium"
                        onClick={() => updateIcalUrl(
                          accommodation.id,
                          isEditing.icalUrl,
                          isEditing.listingId
                        )}
                        disabled={isCurrentlySaving || !isEditing.icalUrl || !isEditing.listingId}
                        style={{ 
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          minWidth: '180px'
                        }}
                      >
                        {isCurrentlySaving ? '⏳ Salvando...' : '💾 SALVA CONFIGURAZIONE'}
                      </Button>
                    </UrlControls>
                  </UrlEditingSection>
                )}
              </AccommodationInfo>

              <Controls>
                <StatusBadge $enabled={accommodation.sync_enabled}>
                  {accommodation.sync_enabled ? (
                    <><CheckCircleIcon size={12} /> Abilitato</>
                  ) : (
                    <><XCircleIcon size={12} /> Disabilitato</>
                  )}
                </StatusBadge>

                {!isEditing && (
                  <Button
                    variant={(!accommodation.airbnb_ical_url || !accommodation.airbnb_listing_id) ? "primary" : "outline"}
                    size="small"
                    onClick={() => startEditingUrls(
                      accommodation.id,
                      accommodation.airbnb_ical_url,
                      accommodation.airbnb_listing_id
                    )}
                    disabled={isCurrentlySaving}
                    style={{ minWidth: '120px', fontWeight: 'bold' }}
                  >
                    {(!accommodation.airbnb_ical_url || !accommodation.airbnb_listing_id) ? 
                      "⚙️ CONFIGURA ICAL" : 
                      "📝 Modifica iCal"
                    }
                  </Button>
                )}

                {/* Pulsante Sincronizza Ora - solo se ha URL iCal */}
                {!isEditing && accommodation.airbnb_ical_url && (
                  <SyncButton
                    variant="secondary"
                    size="small"
                    onClick={() => syncAccommodationNow(accommodation.id)}
                    disabled={syncingAccommodation === accommodation.id || isCurrentlySaving}
                  >
                    {syncingAccommodation === accommodation.id ? (
                      <>⏳ Sincronizzando...</>
                    ) : (
                      <><RefreshIcon size={14} /> Sincronizza Ora</>
                    )}
                  </SyncButton>
                )}

                <Button
                  variant={accommodation.sync_enabled ? 'danger' : 'primary'}
                  size="small"
                  onClick={() => toggleSync(accommodation.id, !accommodation.sync_enabled)}
                  disabled={isCurrentlySaving}
                  style={{ minWidth: '100px' }}
                >
                  {isCurrentlySaving ? 'Salvando...' : 
                   accommodation.sync_enabled ? 'Disabilita' : 'Abilita'}
                </Button>
              </Controls>
            </AccommodationCard>
          );
        })}
      </AccommodationList>

      <div style={{ marginTop: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 1rem 0', color: '#0d9488' }}>💡 Come Configurare la Sincronizzazione:</h4>
        <ol style={{ margin: 0, paddingLeft: '1.5rem', color: '#6b7280', lineHeight: '1.6' }}>
          <li>Clicca <strong>"📅 Configura iCal"</strong> per l'alloggio desiderato</li>
          <li>Inserisci l'<strong>Airbnb Listing ID</strong> (lo trovi nell'URL del tuo listing)</li>
          <li>Per l'URL iCal: vai su <strong>airbnb.com</strong> → Host Dashboard</li>
          <li>Seleziona il listing → <strong>Calendario</strong> → Impostazioni</li>
          <li>Trova <strong>"Esporta calendario"</strong> e copia l'URL iCal</li>
          <li>Incolla l'URL nel campo e clicca <strong>"Salva URL"</strong></li>
          <li>Infine, clicca <strong>"Abilita"</strong> per attivare la sincronizzazione</li>
        </ol>
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '6px', fontSize: '0.875rem' }}>
          <strong>⚠️ Importante:</strong> L'URL iCal permette solo la <em>lettura</em> delle prenotazioni Airbnb. Per bloccare date su Airbnb, dovrai configurare anche l'API Airbnb.
        </div>
      </div>
    </Container>
  );
};

export default AirbnbSyncSettings;